import React from "react";
import "./VerifyUser.scss";
import { SPOTInput } from "spot-component-library/lib/components/form/spotInput/SPOTInput";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useState } from "react";
import userService from "../../services/UserService";
import { Debtor } from "../../types/Debtor";
import { SPOTNotification } from "../notification/SPOTNotification";
import { LinkText } from "../link/LinkText";
import { SPOTCheckBox } from "spot-component-library/lib/components/form/checkbox/SPOTCheckBox";

export interface VerifyUserProps {
    onVerifyResult: (verifiedDebtor: Debtor | null) => void;
    debtNumber: string;
    defaultValue?: Debtor;
}

export const VerifyUser = (props: VerifyUserProps) => {
    const [firstName, setFirstName] = useState<string>(props.defaultValue?.firstName || "");
    const [lastName, setLastName] = useState<string>(props.defaultValue?.lastName || "");
    const [agreed, setAgreed] = useState<boolean>(false);
    const [validationFailed, setValidationFailed] = useState<boolean>(false);

    const { t } = useTranslation();
    const {
        trigger,
        register,
        setError,
        formState: { errors },
    } = useForm({
        mode: "onBlur",
    });

    React.useEffect(() => {
        props.onVerifyResult(null);
    }, [firstName, lastName, agreed]);

    const submitVerifyUser = async () => {
        try {
            setValidationFailed(false);
            const verifiedDebtor = await userService.verifyUser(props.debtNumber, firstName, lastName);
            props.onVerifyResult(verifiedDebtor);
        } catch (e) {
            setValidationFailed(true);
            setError("verifyFirstName", { type: "debtorValidation", message: t("APP_ROOT.VALIDATION.VERIFY_DEBTOR") });
            setError("verifyLastName", { type: "debtorValidation", message: t("APP_ROOT.VALIDATION.VERIFY_DEBTOR") });
        }
    };

    const changeHandler = (e: any) => {
        const target = e.target;
        const name = target.name;
        const value = target.value.trim();

        if (name === "verifyFirstName") {
            setFirstName(value);
        }

        if (name === "verifyLastName") {
            setLastName(value);
        }

        setValidationFailed(false);
        setAgreed(false);
    };

    const changeAgreeHandler = (e: any) => {
        const target = e.target;
        setAgreed(target.checked);

        trigger().then((valid) => {
            if (valid && target.checked) {
                submitVerifyUser();
            }
        });
    };

    return (
        <div>
            <SPOTInput
                dataId="verifyFirstName"
                name="verifyFirstName"
                register={register}
                placeholder={t("PAYMENT_PAGE.OPT_IN.FirstName")}
                onChange={changeHandler}
                label={t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.FIRST_NAME")}
                required={true}
                pattern={{
                    value: /^[^=<>&?!#@%(){};~|+'´`"/\\0123456789]+$/,
                    message: t("APP_ROOT.VALIDATION.MESSAGE_LETTERS"),
                }}
                errors={errors}
                defaultValue={firstName}
            />
            <SPOTInput
                dataId="verifyLastName"
                name="verifyLastName"
                register={register}
                placeholder={t("PAYMENT_PAGE.OPT_IN.LastName")}
                onChange={changeHandler}
                label={t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.LAST_NAME")}
                required={true}
                pattern={{
                    value: /^[^=<>&?!#@%(){};~|+'´`"/\\0123456789]+$/,
                    message: t("APP_ROOT.VALIDATION.MESSAGE_LETTERS"),
                }}
                errors={errors}
                defaultValue={lastName}
            />
            <SPOTCheckBox
                id={"verifyUserCheckbox"}
                name="verifyUserAgree"
                checked={agreed}
                register={register}
                label={
                    <Trans
                        i18nKey="RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.CONFORMATION"
                        values={{
                            debtNumber: props.debtNumber,
                        }}
                        components={{
                            bold: <b />,
                        }}
                    />
                }
                onChange={changeAgreeHandler}
                required={t("APP_ROOT.VALIDATION.MESSAGE_REQUIRED") as string}
                value={agreed ? 1 : 0}
                errors={errors}
                disabled={firstName.trim() === "" || lastName.trim() === ""}
            />

            <br />
            <SPOTNotification
                active={validationFailed}
                background={"filled"}
                type="warning"
                text={
                    <div>
                        <div className={"verify-user-error-text"}>
                            <Trans i18nKey="PAYMENT_PAGE.OPT_IN.SinglePaymentInfoText" />
                        </div>
                        <div className={"verify-user-error-link"}>
                            <Trans
                                i18nKey="PAYMENT_PAGE.OPT_IN.SinglePaymentInfoLink"
                                components={{
                                    link1: <LinkText to={"/?debtno=" + props.debtNumber} title="home" />,
                                }}
                            />
                        </div>
                    </div>
                }
            />
        </div>
    );
};
